/**
 * The admin header's "N live · M marked online". node --test
 *
 * "Marked online" is what the header always showed: Annas whose app last said "online"
 * (the Firestore isOnline flag), approved and not blocked. Nothing switches that flag
 * off when a phone dies or a battery saver kills the app ("zombie online"), so on
 * 2026-10-11 it read 32 while only 13 phones were actually sending GPS.
 *
 * "Live" is the part that is really reachable: a marked-online Anna whose GPS row was
 * written within the last 3 minutes. That is the same line the Live Map draws between a
 * working dot and an orange "reconnecting" one (listenDriverLocations in
 * services/firebase.js), so the two screens agree. Dispatch itself uses its own
 * configurable cutoff (Settings > Dispatch tuning), so a dispatchable Anna can be a bit
 * older than "live" here.
 */

/** A GPS row written within this long counts as live (the Live Map's reconnecting line). */
export const LIVE_GPS_MS = 3 * 60 * 1000;

/**
 * When an Anna's GPS row was last written: the database server's clock (`lastSeen`)
 * when his app sends it, else the phone's own `updatedAt`. The same rule as the Live Map
 * and the server's presence.js, so a phone with a wrong clock is neither shown as live
 * after it has died nor as quiet while it is working.
 */
export const lastSeenMs = (loc) => {
  const server = Number(loc?.lastSeen);
  if (Number.isFinite(server) && server > 0) return server;
  const phone = Number(loc?.updatedAt);
  return Number.isFinite(phone) && phone > 0 ? phone : 0;
};

/** Switched on in the app, approved and not blocked: the number the header always showed. */
export const isMarkedOnline = (driver) => Boolean(driver && driver.isOnline && driver.isApproved && !driver.isBlocked);

/**
 * { marked, live } for the header.
 * `lastSeenById` maps an Anna's id to lastSeenMs of his GPS row. While it is null (not
 * loaded yet, or the read failed) `live` is null and the header falls back to the single
 * marked-online number rather than showing a wrong 0.
 */
export function countOnline({ drivers, lastSeenById, now = Date.now(), liveMs = LIVE_GPS_MS } = {}) {
  const marked = (Array.isArray(drivers) ? drivers : []).filter(isMarkedOnline);
  if (!lastSeenById) return { marked: marked.length, live: null };
  const live = marked.filter((d) => {
    const seenAt = Number(lastSeenById[d.id]);
    return Number.isFinite(seenAt) && seenAt > 0 && now - seenAt <= liveMs;
  }).length;
  return { marked: marked.length, live };
}
